import is from 'ramda/src/is';
import isEmpty from 'ramda/src/isEmpty';
import isNil from 'ramda/src/isNil';

/**
 * @function isArray
 * @param {*} value
 * @return {boolean}
 * @example isArray(null) => false
 * @example isArray(undefined) => false
 * @example isArray({}) => false
 * @example isArray([]) => true
 */
const isArray = value => Array.isArray(value);

/**
 * @function isBoolean
 * Checks the type of input and returns true if it is `Boolean` type
 * @param {*} value
 * @return {boolean}
 * @example isBoolean(3) => false
 * @example isBoolean(false) => true
 * @example isBoolean(true) => true
 */
const isBoolean = value => typeof value === 'boolean';

/**
 * @function isFalsy
 * Checks if a value is falsy -- empty, null, undefined, 0, false
 * @param {*} val
 * @return {boolean}
 * @example isFalsy(0) => true
 * @example isFalsy([]) => true
 * @example isFalsy(true) => false
 * @example isFalsy('test') => false
 */
const isFalsy = val => isNil(val) || isEmpty(val) || !val;

/**
 * @function isFunction
 * Checks if Given Input is `instanceof` Function
 * @param {*} input
 * @return {Boolean}
 * @example isFunction(5) => false
 * @example isFunction(null) => false
 * @example isFunction({}) => false
 * @example isFunction(() => {}) => true
 */
const isFunction = input => input instanceof Function;

/**
 * @function isNilOrEmpty
 * Checks if value is falsy, considering 0 as truthy
 * @param {*} val
 * @return {Boolean}
 * @example isNilOrEmpty('') => true
 * @example isNilOrEmpty(null) => true
 * @example isNilOrEmpty(undefined) => true
 * @example isNilOrEmpty([]) => true
 * @example isNilOrEmpty(0) => false
 * @example isNilOrEmpty(3) => false
 * @example isNilOrEmpty([1, 2, 3]) => false
 */
const isNilOrEmpty = val => isNil(val) || isEmpty(val);

/**
 * @function isNotNilOrEmpty
 * Checks if value is truthy, considering 0 as truthy
 * @param {*} val
 * @return {Boolean}
 * @example isNotNilOrEmpty('') => false
 * @example isNotNilOrEmpty(0) => true
 */
const isNotNilOrEmpty = val => !isNil(val) && !isEmpty(val);

/**
 * Checks the type of input and returns true if it is `Object` type
 * @param {*} value
 * @return {Boolean}
 * @example isObject(3) => false
 * @example isObject(null) => false
 * @example isObject([]) => true (Object.keys and such actions will return an empty array when passed an array)
 * @example isObject({}) => true
 */
const isObject = obj => obj === Object(obj);

/**
 * @function isString
 * Checks the type of the input and return true if it is `String` type
 * @param {*} val
 * @return {boolean}
 * @example isString([1, 2, 3]) => false
 * @example isString('') => true
 */
const isString = val => !isNil(val) && is(String, val);

/**
 * @function isTruthy
 * Checks if the value is truthy -- Not Falsy & not Null
 * @param {*} val
 * @return {boolean}
 * @example isTruthy(0) => false
 * @example isTruthy('') => false
 * @example isTruthy(3) => true
 * @example isTruthy([1, 2, 3]) => true
 */
const isTruthy = val => !isNil(val) && !isFalsy(val);

export {
  isArray,
  isBoolean,
  isFalsy,
  isFunction,
  isNilOrEmpty,
  isNotNilOrEmpty,
  isObject,
  isString,
  isTruthy,
};
